import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { MdSearch, MdInfoOutline, MdContentCopy, MdCheck, MdHistory } from "react-icons/md";

export default function MapMeterRefund() {
  const [meterData, setMeterdatas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [closebanner, setClosebanner] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 5;

  // Track copied status per token index
  const [copiedToken, setCopiedToken] = useState(null);

  const formik = useFormik({
    initialValues: { meterNumber: "" },
    validationSchema: Yup.object({
      meterNumber: Yup.string()
        .required("Meter number is required")
        .min(8, "Meter number must be at least 8 digits"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      setMeterdatas([]);
      setCurrentPage(1);
      
      const endpoint = `https://maprefund.smartpowerbilling.com/api/records/?q=${values.meterNumber}`;
      try {
        const response = await axios.get(endpoint);
        const data = response.data;
        if (data && data.results && data.results.length > 0) {
          // MONTH & YEAR SORTING LOGIC (Most recent first)
          const monthMap = {
            January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
            July: 6, August: 7, September: 8, October: 9, November: 10, December: 11
          };

          const sortedResults = [...data.results].sort((a, b) => {
            // First compare by year descending
            if (b.refund_year !== a.refund_year) {
              return b.refund_year - a.refund_year;
            }
            // If year is the same, compare by month descending
            const monthDiff = (monthMap[b.refund_month] || 0) - (monthMap[a.refund_month] || 0);
            if (monthDiff !== 0) {
              return monthDiff;
            }
            // Fallback to refund_date timestamp if month and year match completely
            return new Date(b.refund_date) - new Date(a.refund_date);
          });

          setMeterdatas(sortedResults);
        } else if(data.r) {
          
        }else{
          setMeterdatas([]);
          setError(response.error);
        }
      } catch (err) {
        setError(
          err.error || "Unable to connect to the server. Please check your internet."
        );
      } finally {
        setLoading(false);
      }
    },
  });

  // AUTO-TRIGGER LOGIC
  useEffect(() => {
    if (formik.values.meterNumber.length >= 8) {
      formik.handleSubmit();
    } else {
      setMeterdatas([]);
      setError("");
    }
  }, [formik.values.meterNumber]);

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentMeterDatas = meterData.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(meterData.length / postsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  // COPY TO CLIPBOARD HANDLER
  const handleCopyToken = (token, index) => {
    if (!token) return;
    navigator.clipboard.writeText(token);
    setCopiedToken(index);
    setTimeout(() => {
      setCopiedToken(null);
    }, 2000);
  };
  
  useEffect(() => {
    async function trackVisit() {
      try {
        await axios.get('https://billadjust.beninelectric.com/track');
      } catch {
        console.log("Failed to track visitor");
      }
    }
    trackVisit();
  }, []);

  const handleCloseBanner = () => {
    setClosebanner(true);
  };

  return (
    <>
      {/* POP-UP BANNER DISPLAYS ONCE THE PAGE LOADS */}
      {!closebanner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 sm:p-6">
          <div className="relative max-w-lg sm:max-w-xl md:max-w-2xl lg:max-w-3xl w-full mx-auto">
            <button
              type="button"
              onClick={handleCloseBanner}
              aria-label="Close banner"
              className="absolute -top-3 -right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-2xl font-bold text-black shadow-lg hover:bg-red-600 hover:text-white transition-colors cursor-pointer"
            >
              &times;
            </button>

            <img
              src="/images/image.png"
              alt="Promotional banner"
              className="block w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-2xl mx-auto"
            />
          </div>
        </div>
      )}

      <div className="min-h-screen bg-white flex flex-col font-sans">
        <main className="flex-grow container mx-auto px-4 py-16">
          <div className="max-w-5xl mx-auto">
            
            {/* BRANDED HEADER */}
            <div className="text-center mb-12">
              <div className="flex justify-center mb-4">
                 <div className="bg-yellow-400 p-3 rounded-full">
                    <MdHistory size={32} className="text-black" />
                 </div>
              </div>
              <h1 className="text-4xl font-black text-black mb-3 uppercase tracking-tight">
                MAP Meter <span className="text-green-700">Refund Portal</span>
              </h1>
              <p className="text-gray-600 max-w-lg mx-auto leading-relaxed">
                Please, note that only eligible and verified MAP metered numbers will produce a refund token.
              </p>
            </div>

            {/* SEARCH CARD */}
            <div className="bg-black rounded-3xl p-1 shadow-2xl mb-12">
              <div className="bg-white rounded-[1.4rem] p-8">
                <form onSubmit={(e) => e.preventDefault()}>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                    Enter Meter Number
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <MdSearch size={24} className="text-green-700 group-focus-within:text-yellow-500 transition-colors" />
                    </div>
                    <input
                      type="text"
                      name="meterNumber"
                      autoComplete="off"
                      placeholder="e.g. 0123456789"
                      value={formik.values.meterNumber}
                      onChange={formik.handleChange}
                      className={`block w-full pl-12 pr-4 py-5 bg-gray-50 text-xl font-mono border-2 rounded-2xl focus:ring-4 focus:ring-green-100 focus:bg-white outline-none transition-all ${
                        formik.touched.meterNumber && formik.errors.meterNumber
                          ? "border-red-400"
                          : "border-gray-100 focus:border-green-700"
                      }`}
                    />
                    {loading && (
                      <div className="absolute inset-y-0 right-4 flex items-center">
                         <div className="w-6 h-6 border-3 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>

                  {formik.values.meterNumber.length > 0 && formik.values.meterNumber.length < 8 && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-yellow-600 font-bold animate-pulse">
                      <MdInfoOutline /> Needs {8 - formik.values.meterNumber.length} more digits to verify...
                    </div>
                  )}
                </form>

                {error && (
                  <div className="mt-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg text-red-700 text-sm font-medium flex items-center gap-3">
                    <MdInfoOutline size={20} /> {error}
                  </div>
                )}
              </div>
            </div>

            {/* RESULTS TABLE/LIST */}
            <div className="min-h-[300px]">
              {!loading && meterData.length === 0 && !error && (
                <div className="text-center py-20 border-2 border-dashed border-gray-100 rounded-3xl">
                  <MdSearch size={60} className="mx-auto text-gray-100 mb-4" />
                  <p className="text-gray-400 font-medium">Results will appear here...</p>
                </div>
              )}

              {!loading && meterData.length > 0 && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="flex items-center justify-between border-b pb-4">
                      <h3 className="text-xl font-bold text-black uppercase tracking-tight">Refund History</h3>
                      <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold uppercase">
                          {meterData.length} Records Found
                      </span>
                  </div>

                  <div className="grid grid-cols-1 gap-6">
                    {currentMeterDatas.map((item, index) => {
                      const globalIndex = indexOfFirstPost + index;
                      const isSuccess = item.status?.toLowerCase() === "success";

                      const refundDay = item.refund_date ? new Date(item.refund_date).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                          : null;

                      return (
                        <div
                          key={globalIndex}
                          className="group relative bg-white border border-gray-100 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
                        >
                          <div className={`absolute top-0 left-0 w-2 h-full transition-colors ${isSuccess ? 'bg-green-700 group-hover:bg-yellow-400' : 'bg-red-500'}`}></div>
                          
                          <div className="p-8">
                            <div className="flex flex-col lg:flex-row justify-between gap-6">
                              
                              <div className="flex-grow">                            
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                                  <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1">Meter Number</p>
                                    <p className="font-mono text-gray-900 font-bold">{item.meter_number}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1">Amount</p>
                                    <p className="text-black font-black">₦{Number(item.amount).toLocaleString()}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1">Meter Cost</p>
                                    <p className="text-black font-black">{item.meter_cost}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1">Balance</p>
                                    <p className="text-black font-black">{item.balance_after}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1">Refund Type</p>
                                    <p className="text-black font-black">{item.arrears_code}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1">Units (kWh)</p>
                                    <p className="text-black font-black">{item.units ?? "N/A"}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1">Refund Month</p>
                                    <p className="text-black font-black">{item.refund_month}</p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1">Date</p>
                                    <p className="text-gray-600 font-bold">
                                      {refundDay ? `${refundDay} ` : ""}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-1">Status</p>
                                    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                      isSuccess ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                      {item.status || "Unknown"}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="lg:w-72">
                                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-2">Token Pin</label>
                                    <div className="text-xl font-mono tracking-[0.2em] text-green-700 font-black break-all">
                                        {item.token || "PENDING"}
                                    </div>
                                    <button 
                                        onClick={() => handleCopyToken(item.token, globalIndex)} 
                                        disabled={!item.token}
                                        className="mt-4 w-full flex items-center justify-center gap-2 bg-yellow-400 hover:bg-black hover:text-white text-black py-2 rounded-xl text-xs font-bold transition-all disabled:opacity-40 disabled:hover:bg-yellow-400 disabled:hover:text-black cursor-pointer"
                                    >
                                        {copiedToken === globalIndex ? (
                                          <>
                                            <MdCheck size={18} className="text-green-700" /> Copied!
                                          </>
                                        ) : (
                                          <>
                                            <MdContentCopy size={18} /> Copy Token
                                          </>
                                        )}
                                    </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* PAGINATION */}
              {meterData.length > postsPerPage && (
                <div className="flex justify-center items-center gap-6 mt-12">
                  <button
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className="w-12 h-12 flex items-center justify-center bg-white border border-gray-200 rounded-full hover:border-green-700 disabled:opacity-30 transition-all shadow-sm cursor-pointer"
                  >
                    ←
                  </button>
                  <div className="text-sm font-bold text-black uppercase tracking-widest">
                    Page <span className="text-green-700">{currentPage}</span> of {totalPages}
                  </div>
                  <button
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                    className="w-12 h-12 flex items-center justify-center bg-white border border-gray-200 rounded-full hover:border-green-700 disabled:opacity-30 transition-all shadow-sm cursor-pointer"
                  >
                    →
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
}